import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { Link } from 'react-router-dom';
import { BORDER, MUTED } from '../pages/Practice/map/mapTokens';
import { CONTENT_MAX_WIDTH } from '../styles/tokens';
import { FOOTER_LINKS } from './home/homeContent';

const legalLinks = [
  { label: 'Terms', href: '/terms_and_conditions' },
  { label: 'Privacy', href: '/privacy_policy' },
];

/**
 * `showSitemap` is opt-in rather than always-on: this footer renders on every
 * page in the app, and a four-column link block belongs at the end of the
 * marketing homepage, not under a flashcard drill.
 */
const Footer = ({ showSitemap = false }) => {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'rgba(245,245,245,0.95)',
        backdropFilter: 'blur(8px)',
        borderTop: '1px solid #e0eaf5',
        py: showSitemap ? { xs: 4, md: 5 } : 1.25,
        px: { xs: 2, md: 4 },
        mt: 'auto',
        // This footer is often placed inside a column layout. Do not let that
        // layout stretch it to the viewport height on narrow screens.
        flex: '0 0 auto',
        minHeight: 0,
        position: 'relative',
        zIndex: 2,
      }}
    >
      {showSitemap ? (
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 3, md: 4 },
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            maxWidth: CONTENT_MAX_WIDTH,
            mx: 'auto',
            mb: 4,
            pb: 3,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          {FOOTER_LINKS.map((group) => (
            <Box key={group.heading}>
              <Typography
                component="h2"
                sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.875rem', mb: 1.25 }}
              >
                {group.heading}
              </Typography>
              <Stack spacing={0.75}>
                {group.links.map((link) => (
                  <Link
                    key={link.label}
                    to={link.to}
                    style={{ textDecoration: 'none', color: MUTED, fontSize: '0.875rem' }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>
      ) : null}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 0.75, md: 2 }}
        alignItems="center"
        justifyContent={{ xs: 'center', md: 'space-between' }}
        sx={{ flex: '0 0 auto', minHeight: 0, maxWidth: CONTENT_MAX_WIDTH, mx: 'auto', width: '100%' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, order: { xs: 1, md: 1 } }}>
          <Typography
            translate="no"
            className="notranslate"
            fontWeight={900}
            color="primary.main"
            sx={{ fontSize: '0.95rem', lineHeight: 1.2 }}
          >
            FIDE Prep
          </Typography>
        </Box>

        {/* The independence disclaimer lives in <Disclaimer /> on the pages that
            need it (APP-53); the footer only carries the legal chrome. */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flexShrink: 0, textAlign: 'center', order: { xs: 3, md: 2 } }}
        >
          © 2026 · All rights reserved
        </Typography>

        <Stack direction="row" spacing={2} sx={{ flexShrink: 0, order: { xs: 2, md: 3 } }}>
          {legalLinks.map((link) => (
            <Link key={link.label} to={link.href} style={{ textDecoration: 'none', color: 'inherit', fontWeight: 700 }}>
              {link.label}
            </Link>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

export default Footer;
